import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { parseISO } from 'date-fns';
import { useReactAppState } from '../../shared/containers/utils';
import useMLSMatch from '../../mls-match-list/hooks/useMLSMatch';
import {
  apiURL,
  d3SingleMatchAPI,
  forgeMatchContentVideosAPI,
  forgeMatchVideosAPI,
  mlsGetMatches,
} from '../../shared/api/variables';
import { fetchClient } from '../../shared/api/fetching';
import { composeVideoList } from '../../mls-match-feed/utils';
import { highlightTypes, matchHighlightsTagSlug } from '../../shared/utils';
import {
  composeMatchStatus,
  filterMlsMatchesByCompetitionsToSkip,
  periodTypes,
  statusTypes,
} from '../../mls-match-list/utils';
import { formatImageUrl, getMatchesUrl } from '../../shared/utils';
import { formatMatchMinutes } from '../../mls-match-summary/utils';
import { useLocalMatchDateTime } from '../../mls-match-list/utils';
import { getDateTranslation } from '../../mls-vertical-scoreboard/utils';
import { dateWithBeforeAndAfter } from '../../shared/containers/ReactApp/MatchHandler';
import { formatStatsAPIMatches } from '../../mls-horizontal-scoreboard/utils';
import useScheduledMatchRefetch from '../../shared/hooks/useScheduledMatchRefetch';

function usePriorityMatch({ matchSportecId, matchSlug }) {
  const { t, apiList, urlList, directoryList, videoTags } = useReactAppState();
  const { d3SportsAPI } = apiList || {};
  const queryClient = useQueryClient();
  const [refetchInterval, setRefetchInterval] = useState(60000);
  const [matchStatus, setMatchStatus] = useState(null);

  const {
    data: statsAPIMatch,
    isLoading: isLoadingStatsAPIMatch,
    isError: isStatsAPIMatchError,
  } = useMLSMatch({
    matchSportecId,
    options: { refetchInterval },
  });

  const matchEndpoint = d3SingleMatchAPI({ matchSportecId });

  const { data: forgeMatch, isLoading: isLoadingForgeMatch } = useQuery({
    queryKey: ['d3-match', matchSportecId],
    queryFn: () =>
      fetchClient({ endpoint: matchEndpoint, apiURL: d3SportsAPI }),
    enabled: !!matchSportecId,
  });

  const { competition_label } = statsAPIMatch?.match_information || {};

  const { matchDate: forgeMatchDate, leagueMatchTitle } = forgeMatch || {};
  useEffect(() => {
    const { match_status } = statsAPIMatch?.match_information || {};
    const status = composeMatchStatus({ period: match_status });
    setMatchStatus(status);
    if (!!refetchInterval && status?.abbreviation !== periodTypes.live) {
      setRefetchInterval(null);
    }
  }, [statsAPIMatch]);

  // --- Fallback: date-range fetch when the primary stats API errors ---
  // Mirrors the MatchHandler pattern used on the match hub.
  // Prefer the forge match date (available independently of stats API errors).
  const fallbackDate = useMemo(() => {
    const date =
      statsAPIMatch?.match_information?.planned_kickoff_time || forgeMatchDate;
    return date ? new Date(date) : new Date();
  }, [forgeMatchDate, statsAPIMatch?.match_information?.planned_kickoff_time]);

  const { dayBefore, dayAfter } = useMemo(
    () => dateWithBeforeAndAfter(fallbackDate),
    [fallbackDate]
  );

  const seasonSportecId =
    statsAPIMatch?.match_information?.season_id ||
    window?.forgeVariables?.currentSite?.currentSeasonSportecId;

  const fallbackEndpoint = mlsGetMatches({
    from: dayBefore,
    to: dayAfter,
    seasonSportecId,
  });

  const { data: matchByDate } = useQuery({
    queryKey: ['mls-matches-date-range', { from: dayBefore, to: dayAfter }],
    queryFn: () =>
      fetchClient({ endpoint: fallbackEndpoint, apiURL: apiList?.statsAPI }),
    refetchOnWindowFocus: false,
    select: (response) => {
      if (!response?.schedule) return null;
      const sorted = [...response.schedule].sort(
        (a, b) =>
          parseISO(a.planned_kickoff_time) - parseISO(b.planned_kickoff_time)
      );
      const matches = formatStatsAPIMatches(
        filterMlsMatchesByCompetitionsToSkip(sorted)
      );
      return matches.find((m) => m.match_id === matchSportecId) || null;
    },
    enabled: !!isStatsAPIMatchError && !!seasonSportecId,
    staleTime: Infinity,
  });

  // Seed the primary query cache so useMLSMatch picks up the fallback data
  useEffect(() => {
    if (isStatsAPIMatchError && matchByDate) {
      queryClient.setQueryData(['mls-match', matchSportecId], matchByDate);
    }
  }, [matchByDate, matchSportecId, isStatsAPIMatchError]);

  // Schedule a refetch 60 min before kickoff (only needed when using fallback data)
  useScheduledMatchRefetch({
    matchDate: matchByDate?.planned_kickoff_time || forgeMatchDate,
    minutesBefore: 60,
    queryKey: ['mls-match', matchSportecId],
    queryClient,
    enabled:
      !!isStatsAPIMatchError &&
      !!(forgeMatchDate || matchByDate?.planned_kickoff_time),
  });

  const isLoading = isLoadingStatsAPIMatch || isLoadingForgeMatch;
  const isReady =
    !isLoading && !!statsAPIMatch && !!matchSportecId && !!matchSlug;

  const {
    away: {
      abbreviation: awayAbbreviation,
      logoColorUrl: awayLogoColorUrl,
      backgroundColor: awayBackgroundColor,
    } = {},
    home: {
      abbreviation: homeAbbreviation,
      logoColorUrl: homeLogoColorUrl,
      backgroundColor: homeBackgroundColor,
    } = {},
    leaguePromoImage: { assetUrl: leaguePromoImage } = {},
    appleStreamURL,
    slug: forgeSlug,
    competition: forgeCompetition,
    season: forgeSeason,
  } = forgeMatch || { away: {}, home: {} };

  const {
    minute_of_play,
    planned_kickoff_time,
    kickoff_time,
    home_team_goals: homeGoals,
    away_team_goals: awayGoals,
    match_date_time_status,
  } = statsAPIMatch?.match_information || {};

  const showScore =
    matchStatus?.abbreviation === statusTypes.live ||
    matchStatus?.abbreviation === statusTypes.post ||
    matchStatus?.abbreviation === statusTypes.finalWhistle;

  const isPost = matchStatus?.abbreviation === statusTypes.post;

  const forgeMatchVideosEndpoint = forgeMatchVideosAPI({ matchSportecId });
  const forgeMatchContentVideosEndpoint = forgeMatchContentVideosAPI({
    matchSlug: forgeSlug || matchSlug,
  });

  const { data: forgeMatchVideos } = useQuery({
    queryKey: [`forge-match-videos`, { matchSportecId }],
    queryFn: () =>
      fetchClient({
        apiURL: apiURL.forgeDAPI,
        endpoint: forgeMatchVideosEndpoint,
      }),
    select: (videos) => videos?.items,
    enabled: !!matchSportecId && isPost,
    refetchOnWindowFocus: false,
  });

  const { data: forgeMatchContentVideos } = useQuery({
    queryKey: [`forge-match-content-videos`, { slug: forgeSlug || matchSlug }],
    queryFn: () =>
      fetchClient({
        apiURL: apiURL.forgeDAPI,
        endpoint: forgeMatchContentVideosEndpoint,
      }),
    select: (videos) => videos?.items,
    enabled: !!(forgeSlug || matchSlug) && isPost,
    refetchOnWindowFocus: false,
  });

  const videoList = useMemo(() => {
    const matchVideos =
      forgeMatchVideos?.length >= 0 && forgeMatchContentVideos?.length >= 0
        ? [...forgeMatchVideos, ...forgeMatchContentVideos]
        : [];
    return composeVideoList(matchVideos, videoTags);
  }, [forgeMatchVideos, forgeMatchContentVideos, videoTags]);

  const gameHighlights = videoList.find(
    (video) =>
      video?.fields?.highlightType === highlightTypes.gameHighlights ||
      video?.tags?.find((tag) => tag?.slug === matchHighlightsTagSlug)
  );
  const fullGame = videoList.find(
    (video) => video?.fields?.highlightType === highlightTypes.fullGame
  );
  const condensedGame = videoList.find(
    (video) => video?.fields?.highlightType === highlightTypes.condensedGame
  );

  const hasHighlights =
    isPost && !!(gameHighlights || fullGame || condensedGame);

  const { baseUrl } = urlList || {};
  const { matchHubDir } = directoryList || {};
  const { slug: competitionSlug } = forgeCompetition || {};
  const { name: seasonYear } = forgeSeason || {};

  const matchPageUrl = getMatchesUrl(
    baseUrl,
    matchHubDir,
    competitionSlug,
    seasonYear,
    forgeSlug
  );

  const highlightsUrl = matchPageUrl
    ? `${matchPageUrl}?openHighlights=true`
    : null;

  const [minutes, extraMinutes] = formatMatchMinutes(minute_of_play);

  const { highQuality: homeLogoUrl } = isReady
    ? formatImageUrl(homeLogoColorUrl, {
        format: 'w_128,h_128,c_pad',
        extension: 'f_auto',
      }) || { highQuality: '/assets/icons/tbd.png' }
    : {};

  const { highQuality: awayLogoUrl } = isReady
    ? formatImageUrl(awayLogoColorUrl, {
        format: 'w_128,h_128,c_pad',
        extension: 'f_auto',
      }) || { highQuality: '/assets/icons/tbd.png' }
    : {};

  const { highQuality: leaguePromoImageUrl } = leaguePromoImage
    ? formatImageUrl(leaguePromoImage, {
        format: 'q_auto:best',
        extension: 'f_webp',
      })
    : {};

  const localTime = kickoff_time || planned_kickoff_time;
  const dateLabel = getDateTranslation(localTime, t, false);
  const matchDateTime = useLocalMatchDateTime(localTime);
  // Prefer actual kickoff time for clock display; fall back to planned
  const matchTime = matchDateTime?.time ?? null;
  const isTimeTbd =
    match_date_time_status?.toLowerCase() === 'time tbd' ||
    match_date_time_status === 'Day and time tbd';

  return {
    isReady,
    matchStatus,
    homeAbbreviation,
    homeLogoUrl,
    homeBackgroundColor,
    homeGoals,
    awayAbbreviation,
    awayLogoUrl,
    awayBackgroundColor,
    awayGoals,
    showScore,
    hasHighlights,
    appleStreamURL,
    highlightsUrl,
    leaguePromoImageUrl,
    minutes,
    extraMinutes,
    dateLabel,
    matchTime,
    isTimeTbd,
    leagueMatchTitle: leagueMatchTitle || competition_label || '',
    matchPageUrl,
  };
}

export default usePriorityMatch;
